/**
 * What-if: choose which antennas and missions take part in the analysis,
 * apply a preset, re-run, and save or compare scenarios in D1. Rows follow
 * the density setting; an antenna disabled in the network data says "off"
 * rather than fading below readable contrast.
 */
import { useMemo, useState } from 'react';
import { Play, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Chip, StatusIcon, TextButton } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { loaderApi } from '../../data/api';
import { PlacedAntennasSection } from '../../place/PlacedAntennasSection';
import { defaultActiveMissionIds, useLoader } from '../../store';
import type { SavedScenario, ScenarioPreset } from '../../types';
import { pct, slaTone } from '../format';

function ToggleRow({
  label,
  checked,
  onChange,
  meta,
  dim,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  meta?: string;
  dim?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-control px-2 py-cell-y hover:bg-overlay">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-3.5 w-3.5 accent-[var(--color-accent)]"
      />
      <span className={cn('truncate', dim ? 'text-ink-3' : 'text-ink')}>{label}</span>
      {dim && <span className="text-data-sm uppercase tracking-wider text-ink-3">off</span>}
      {meta && <span className="ml-auto text-data-sm text-ink-3">{meta}</span>}
    </label>
  );
}

/** A saved scenario's overall SLA: the percentage with its status icon and word. */
function ScenarioSla({ overallSLA }: { overallSLA: number }) {
  const status = overallSLA >= 95 ? 'met' : overallSLA >= 70 ? 'partial' : 'unmet';
  return (
    <Chip tone={slaTone(status)}>
      <StatusIcon tone={slaTone(status)} label={status} />
      {pct(overallSLA)}
    </Chip>
  );
}

export function WhatIfPanel() {
  const dataset = useLoader((s) => s.dataset);
  const network = useLoader((s) => s.network);
  const results = useLoader((s) => s.results);
  const timeframe = useLoader((s) => s.timeframe);
  const activeMissionIds = useLoader((s) => s.activeMissionIds);
  const activeAntennaIds = useLoader((s) => s.activeAntennaIds);
  const setActiveMissionIds = useLoader((s) => s.setActiveMissionIds);
  const setActiveAntennaIds = useLoader((s) => s.setActiveAntennaIds);
  const toggleActiveMission = useLoader((s) => s.toggleActiveMission);
  const toggleActiveAntenna = useLoader((s) => s.toggleActiveAntenna);
  const requestAnalysis = useLoader((s) => s.requestAnalysis);
  const patchDataset = useLoader((s) => s.patchDataset);
  const [baseline, setBaseline] = useState<SavedScenario | null>(null);
  const [saving, setSaving] = useState(false);

  const antennaGroups = useMemo(() => {
    const map = new Map<string, typeof antennas>();
    const antennas = dataset?.antennas ?? [];
    for (const a of antennas) {
      const key = a.network || a.location.name || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [dataset]);

  const missionsByTier = useMemo(() => {
    const map = new Map<number, NonNullable<typeof dataset>['missions']>();
    for (const m of dataset?.missions ?? []) {
      if (!map.has(m.priorityTier)) map.set(m.priorityTier, []);
      map.get(m.priorityTier)!.push(m);
    }
    return [...map.entries()].sort(([a], [b]) => a - b);
  }, [dataset]);

  if (!dataset) return <div className="p-4 text-xs text-ink-3">Loading the network.</div>;

  const activeM = new Set(activeMissionIds);
  const activeA = new Set(activeAntennaIds);

  const applyPreset = (p: ScenarioPreset) => {
    if (p.activeMissions)
      setActiveMissionIds(p.activeMissions.filter((id) => dataset.missions.some((m) => m.id === id)));
    if (p.activeAntennas)
      setActiveAntennaIds(p.activeAntennas.filter((id) => dataset.antennas.some((a) => a.id === id)));
    if (p.addedMission && !dataset.missions.some((m) => m.id === p.addedMission!.id)) {
      patchDataset({ missions: [...dataset.missions, p.addedMission] });
    }
    if (p.overrides) {
      const missions = dataset.missions.map((m) =>
        p.overrides![m.id] ? { ...m, requirements: { ...m.requirements, ...p.overrides![m.id] } } : m,
      );
      patchDataset({ missions });
    }
    requestAnalysis();
    toast('info', `Preset applied: ${p.name}`);
  };

  const reset = () => {
    setActiveMissionIds(defaultActiveMissionIds(dataset.missions));
    setActiveAntennaIds(dataset.antennas.filter((a) => a.enabled !== false).map((a) => a.id));
    requestAnalysis();
  };

  const save = async () => {
    if (!results) {
      toast('warn', 'Run the analysis before saving a scenario.');
      return;
    }
    const name = window.prompt('Scenario name');
    if (!name) return;
    const scenario: SavedScenario = {
      id: `scn_${Date.now().toString(36)}`,
      name,
      description: `${activeMissionIds.length} missions, ${activeAntennaIds.length} antennas, ${timeframe.hours} h`,
      config: {
        activeMissions: activeMissionIds,
        activeAntennas: activeAntennaIds,
        networkMode: network,
        timeframe: {
          start: new Date(timeframe.startMs).toISOString(),
          end: new Date(timeframe.endMs).toISOString(),
        },
      },
      results: {
        overallSLA: results.summary.overallSLA,
        missionSLA: Object.fromEntries(
          Object.entries(results.missionSLA).map(([id, s]) => [
            id,
            { percentage: s.percentage, status: s.status, totalMinutes: s.totalMinutes, passes: s.passes },
          ]),
        ),
        totalConflicts: results.summary.totalConflicts,
        resolvedConflicts: results.summary.resolvedConflicts,
      },
    };
    setSaving(true);
    try {
      await loaderApi.saveScenario(network, scenario);
      patchDataset({ scenarios: [...dataset.scenarios, scenario] });
      toast('success', `Saved scenario ${name}.`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast('error', `Could not save the scenario: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: SavedScenario) => {
    if (!window.confirm(`Delete scenario ${s.name}?`)) return;
    try {
      await loaderApi.deleteScenario(network, s.id);
      patchDataset({ scenarios: dataset.scenarios.filter((x) => x.id !== s.id) });
      if (baseline?.id === s.id) setBaseline(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast('error', `Could not delete the scenario: ${message}`);
    }
  };

  const load = (s: SavedScenario) => {
    setActiveMissionIds(s.config.activeMissions.filter((id) => dataset.missions.some((m) => m.id === id)));
    setActiveAntennaIds(s.config.activeAntennas.filter((id) => dataset.antennas.some((a) => a.id === id)));
    requestAnalysis();
  };

  return (
    <div className="flex flex-col gap-section p-panel text-data">
      <div className="flex flex-wrap gap-1">
        <TextButton onClick={requestAnalysis} className="flex items-center gap-1 bg-accent-soft text-accent">
          <Play className="h-3.5 w-3.5" /> Re-run
        </TextButton>
        <TextButton onClick={reset} className="flex items-center gap-1">
          <RotateCcw className="h-3.5 w-3.5" /> Reset
        </TextButton>
        <TextButton
          onClick={() => void save()}
          disabled={saving || dataset.source === 'static'}
          className="flex items-center gap-1"
          title={
            dataset.source === 'static'
              ? 'Saving needs the database'
              : 'Save the current analysis as a scenario'
          }
        >
          <Save className="h-3.5 w-3.5" /> Save scenario
        </TextButton>
      </div>

      {dataset.presets.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Presets</div>
          <div className="flex flex-wrap gap-1">
            {dataset.presets.map((p) => (
              <button
                key={p.id}
                type="button"
                title={p.description}
                onClick={() => applyPreset(p)}
                className="rounded-control border border-hairline px-2 py-1 text-ink-2 hover:border-hairline-strong hover:text-ink"
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {dataset.scenarios.length > 0 && (
        <div>
          <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Saved scenarios</div>
          <ul className="flex flex-col gap-1">
            {dataset.scenarios.map((s) => {
              const delta = results && s.results ? results.summary.overallSLA - s.results.overallSLA : null;
              return (
                <li
                  key={s.id}
                  className={cn(
                    'flex items-center gap-2 rounded-control border border-hairline px-2 py-cell-y',
                    baseline?.id === s.id && 'border-accent/50',
                  )}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left text-ink hover:text-accent"
                    onClick={() => load(s)}
                    title={s.description}
                  >
                    {s.name}
                  </button>
                  {s.results && <ScenarioSla overallSLA={s.results.overallSLA} />}
                  {delta !== null && (
                    <span
                      className={cn(
                        'font-mono',
                        delta > 0 ? 'text-nominal' : delta < 0 ? 'text-warning' : 'text-ink-3',
                      )}
                      title="Current overall SLA versus this scenario"
                    >
                      {delta > 0 ? '+' : ''}
                      {Math.round(delta)}
                    </span>
                  )}
                  <button
                    type="button"
                    className={cn('rounded-control px-1 hover:text-ink', baseline?.id === s.id ? 'text-accent' : 'text-ink-3')}
                    title="Use as comparison baseline"
                    aria-label={`vs: compare with ${s.name}`}
                    aria-pressed={baseline?.id === s.id}
                    onClick={() => setBaseline(baseline?.id === s.id ? null : s)}
                  >
                    vs
                  </button>
                  <button
                    type="button"
                    className="rounded-control text-ink-3 hover:text-warning"
                    aria-label={`Delete scenario ${s.name}`}
                    onClick={() => void remove(s)}
                  >
                    <Trash2 aria-hidden className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <PlacedAntennasSection />

      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-3">
          <span>
            Antennas ({activeAntennaIds.length} of {dataset.antennas.length})
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="hover:text-ink"
              onClick={() => setActiveAntennaIds(dataset.antennas.map((a) => a.id))}
            >
              All
            </button>
            <button type="button" className="hover:text-ink" onClick={() => setActiveAntennaIds([])}>
              None
            </button>
          </span>
        </div>
        {antennaGroups.map(([group, list]) => (
          <div key={group} className="mb-1">
            <div className="px-2 py-0.5 text-[10px] font-semibold text-ink-2">{group}</div>
            {list.map((a) => (
              <ToggleRow
                key={a.id}
                label={a.shortName}
                meta={a.capabilities.diameterM ? `${a.capabilities.diameterM} m` : undefined}
                checked={activeA.has(a.id)}
                onChange={() => toggleActiveAntenna(a.id)}
                dim={a.enabled === false}
              />
            ))}
          </div>
        ))}
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-3">
          <span>
            Missions ({activeMissionIds.length} of {dataset.missions.length})
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="hover:text-ink"
              onClick={() => setActiveMissionIds(dataset.missions.map((m) => m.id))}
            >
              All
            </button>
            <button type="button" className="hover:text-ink" onClick={() => setActiveMissionIds([])}>
              None
            </button>
          </span>
        </div>
        {missionsByTier.map(([tier, list]) => (
          <div key={tier} className="mb-1">
            <div className="px-2 py-0.5 text-[10px] font-semibold text-ink-2">
              Tier {tier}
              {dataset.tiers.find((t) => t.level === tier)
                ? `, ${dataset.tiers.find((t) => t.level === tier)!.name}`
                : ''}
            </div>
            {list.map((m) => (
              <ToggleRow
                key={m.id}
                label={m.shortName}
                meta={m.orbit.type ? String(m.orbit.type).toUpperCase() : undefined}
                checked={activeM.has(m.id)}
                onChange={() => toggleActiveMission(m.id)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
